'use client';

import React, { FC, ReactNode } from 'react';
import { TAnalyticArgs } from '@/containers/AnalyticsContainer';
import { useGetUser } from '@/queries/user';
import { useLogin } from '@/modules/register/hooks';
import BottomSheet from '@/components/BottomSheet';
import Button from '@/components/Button/Button';
import { Divider } from '@/components/Divider';
import { useHandleBeforeLogin } from './hooks/useHandleBeforeLogin';
import { useHandleStatusBottomSheetMessage } from './hooks/useHandleStatusBottomSheetMessage';
import { useRegState } from '@/modules/register/hooks/useRegState';
import { TUseSetUserStatusData } from '@/userStatus/hooks';
import { classnames } from '@/utils/classnames';
import { UserStatusBottomSheetDefaultChildren } from './UserStatusBottomSheetDefaultChildren';

export type TUserStatusBottomSheetData = Omit<
  TUseSetUserStatusData,
  'status' | 'isVerified'
> & {
  status: Exclude<TUseSetUserStatusData['status'], 'VERIFIED'>;
};

export interface IUserStatusBottomSheetContainerProps
  extends TUserStatusBottomSheetData {
  children?:
    | ReactNode
    | ((__userStatusData: TUserStatusBottomSheetData) => ReactNode);
}

export const UserStatusBottomSheetContainer: FC<
  IUserStatusBottomSheetContainerProps
> = ({
  children = UserStatusBottomSheetDefaultChildren,
  ...userStatusData
}) => {
  const { data: user } = useGetUser({
    retry: false,
  });

  // Call RegState API only if user is logged in and national ID is unverified
  const shouldCallRegState =
    !userStatusData.isAnonymous && !user?.nationalIdVerified;

  const {
    data: regState,
    isLoading: isRegStateLoading,
    registerMessage,
    isRegisterCompleted,
  } = useRegState({
    enabled: shouldCallRegState,
    retry: false,
  });

  const isDisallowedActionBottomSheet = typeof children === 'function';

  let returnButtonAnalyticEvent: TAnalyticArgs;
  let loginAnalyticEvent: TAnalyticArgs;
  let completeRegisterAnalyticEvent: TAnalyticArgs;

  if (isDisallowedActionBottomSheet) {
    returnButtonAnalyticEvent = userStatusData.isAnonymous
      ? ['ExpModeA_DisallowedActionBottomSheets_click_return']
      : ['ExpModeG_DisallowedActionBottomSheets_click_return'];
    loginAnalyticEvent = ['ExpModeA_DisallowedActionBottomSheets_click_login'];
    completeRegisterAnalyticEvent = [
      'ExpModeG_DisallowedActionBottomSheets_click_login',
    ];
  } else {
    returnButtonAnalyticEvent = userStatusData.isAnonymous
      ? ['ExpModeA_FirstBottomSheet_click_return']
      : ['ExpModeG_FirstBottomSheet_click_return'];
    loginAnalyticEvent = ['ExpModeA_FirstBottomSheet_click_login'];
    completeRegisterAnalyticEvent = ['ExpModeG_FirstBottomSheet_click_return'];
  }

  const { handleBeforeLogin } = useHandleBeforeLogin(regState);
  const { handleUtmLogin } = useLogin();

  const { returnMessageLayout } = useHandleStatusBottomSheetMessage(
    registerMessage,
    children,
    userStatusData
  );

  const handleLogin = () => {
    handleBeforeLogin();
    handleUtmLogin();
  };

  return (
    <div className="flex flex-col gap-y-4">
      <div>{returnMessageLayout()}</div>
      <Divider includedWidth={40} />
      <div className="flex flex-col gap-y-1" onClick={BottomSheet.close}>
        {userStatusData.isAnonymous && (
          <Button
            analytics={loginAnalyticEvent}
            className={classnames(
              'col-span-1',
              isRegisterCompleted && 'pointer-events-none'
            )}
            disabled={isRegisterCompleted}
            id={loginAnalyticEvent[0]}
            isFullWidth
            onClick={handleLogin}
          >
            ورود | ثبت نام
          </Button>
        )}
        {userStatusData.isGuest && (
          <div
            className={classnames(isRegisterCompleted && 'pointer-events-none')}
          >
            <Button
              analytics={completeRegisterAnalyticEvent}
              className="col-span-1"
              disabled={isRegisterCompleted || isRegStateLoading}
              id={completeRegisterAnalyticEvent[0]}
              isFullWidth
              onClick={handleBeforeLogin}
            >
              تکمیل ثبت نام
            </Button>
          </div>
        )}
        <Button
          analytics={returnButtonAnalyticEvent}
          id={returnButtonAnalyticEvent[0]}
          variant="link"
          isFullWidth
        >
          بازگشت
        </Button>
      </div>
    </div>
  );
};
